import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { audit } from "../../lib/audit";
import { vigilantes, usuarios } from "../../data/mock";
import { loginTsKey } from "../../lib/turno";
import { Camera, Check, ChevronRight, ChevronLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const EMPRESA_ID  = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const BUCKET      = "passagem-fotos";

async function fecharPlantaoAtivo(userId) {
  const { data } = await supabase
    .from("liberty_plantoes")
    .select("id, inicio")
    .eq("empresa_id", EMPRESA_ID)
    .eq("vigilante_id", userId)
    .is("fim", null)
    .maybeSingle();
  if (!data) return;
  const duracaoMin = Math.round((Date.now() - new Date(data.inicio).getTime()) / 60000);
  await supabase.from("liberty_plantoes").update({
    fim:             new Date().toISOString(),
    duracao_minutos: duracaoMin,
  }).eq("id", data.id);
}

const MATERIAL_LISTA = [
  "Chave do portão de acesso lateral",
  "Chave porta de vidro",
  "Chave depósito 1",
  "Chave depósito 2",
  "Chave Subestação 1º piso",
  "Chave amarela (porta área dos depósitos)",
  "01 livro de ocorrência",
  "01 pasta com documentos",
  "Celular corporativo",
  "Carregador do celular corporativo",
];

const PRIOR_PASS = [
  { value: "baixa", label: "🟢 Baixa", bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D" },
  { value: "media", label: "🟡 Média", bg: "#FFFBEB", border: "#FDE68A", text: "#92400E" },
  { value: "alta",  label: "🔴 Alta",  bg: "#FEF2F2", border: "#FECACA", text: "#991B1B" },
];

const materialInicial = () =>
  Object.fromEntries(MATERIAL_LISTA.map((item) => [item, false]));

export default function PassagemPonto() {
  const { user, logout } = useApp();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [prioridade, setPrioridade] = useState("media");
  const [paraVigilanteNome, setParaVigilanteNome] = useState("");
  const [material, setMaterial] = useState(materialInicial);
  const [observacoes, setObservacoes] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [fotoAmpliada, setFotoAmpliada] = useState(false);

  const fileRef = useRef(null);

  const vigilante = vigilantes.find((v) => v.id === user?.id);
  const matriculaExibir = vigilante?.nr_matricula || vigilante?.matricula || null;

  const optsVigilantes = usuarios
    .filter((u) => u.tipo === "vigilante" && u.nome !== user?.nome)
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const handleFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setFotoFile(file);
    setFotoPreview(URL.createObjectURL(file));
  };

  const toggleItem = (item) =>
    setMaterial((m) => ({ ...m, [item]: !m[item] }));

  const irParaStep2 = () => {
    if (!fotoPreview) { toast.error("Tire uma foto para continuar"); return; }
    setStep(2);
  };

  const irParaStep3 = () => {
    if (!titulo.trim()) { toast.error("Informe o título da passagem"); return; }
    setStep(3);
  };

  const confirmar = async () => {
    setSaving(true);
    try {
      let fotoUrl = null;
      if (fotoFile) {
        const ext = (fotoFile.name.split(".").pop() || "jpg").toLowerCase();
        const path = `${user?.id}_${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, fotoFile, { contentType: fotoFile.type || "image/jpeg" });
        if (!upErr) {
          const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
          fotoUrl = urlData?.publicUrl || null;
        }
      }

      const materialConfirmado = MATERIAL_LISTA.filter((item) => material[item]);

      await supabase.from("liberty_passagem_turno").insert({
        de_vigilante_nome:  user?.nome,
        de_vigilante_id:    user?.id,
        matricula:          matriculaExibir,
        tipo_plantao:       vigilante?.turno_inicio >= "19:00" ? "noturno" : "diurno",
        horario_inicio:     vigilante?.turno_inicio || null,
        horario_fim:        vigilante?.turno_fim    || null,
        foto_url:           fotoUrl,
        material_confirmado: materialConfirmado,
        titulo:             titulo.trim(),
        descricao:          descricao.trim() || null,
        prioridade,
        observacoes:        observacoes.trim() || null,
        para_vigilante_nome: paraVigilanteNome.trim() || null,
        empresa_id:         EMPRESA_ID,
      });

      await fecharPlantaoAtivo(user?.id);

      audit({
        usuario_nome: user?.nome,
        usuario_role: user?.role,
        acao: "passagem_ponto",
        modulo: "Passagem de Turno",
        descricao: `${user?.nome} registrou passagem de ponto`,
      });

      if (user?.id) localStorage.removeItem(loginTsKey(user.id));

      setDone(true);
      setTimeout(() => { logout(); navigate("/login"); }, 2500);
    } catch (err) {
      console.error("[PassagemPonto]", err);
      toast.error("Erro ao salvar passagem. Tente novamente.");
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div
        className="h-[100dvh] bg-[#f4f5f8] flex flex-col items-center justify-center px-6 gap-4 text-center"
        style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
      >
        <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-600" />
        </div>
        <p className="text-2xl font-bold text-[#333A56]">Passagem registrada!</p>
        <p className="text-sm text-[#64748b]">Encerrando sessão…</p>
      </div>
    );
  }

  const priorCfg = PRIOR_PASS.find((p) => p.value === prioridade);

  return (
    <MobileLayout title="Passagem de Ponto" scrollable>
      {/* Barra de progresso */}
      <div className="flex items-center gap-1.5 mb-5">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`flex-1 h-1.5 rounded-full transition-all ${
              s <= step ? "bg-[#333A56]" : "bg-[#e8eaf0]"
            }`}
          />
        ))}
      </div>

      {/* ── Passo 1: Foto ── */}
      {step === 1 && (
        <div className="flex flex-col gap-4">
          <div className="text-center">
            <p className="font-bold text-lg text-[#333A56]">Foto de assunção</p>
            <p className="text-sm text-[#64748b] mt-1">
              Tire uma selfie para registrar sua passagem de ponto
            </p>
          </div>

          <div
            className="w-full rounded-3xl overflow-hidden border-2 border-dashed border-[#e8eaf0] bg-white flex items-center justify-center cursor-pointer active:scale-[0.99] transition"
            style={{ minHeight: 240 }}
            onClick={() => fileRef.current?.click()}
          >
            {fotoPreview ? (
              <img src={fotoPreview} alt="preview" className="w-full object-cover" style={{ maxHeight: 300 }} />
            ) : (
              <div className="flex flex-col items-center gap-3 py-12 text-[#64748b]">
                <Camera className="w-16 h-16 text-[#e8eaf0]" />
                <p className="text-sm font-medium">Toque para abrir a câmera</p>
              </div>
            )}
          </div>

          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFoto} />

          {fotoPreview && (
            <button type="button" onClick={() => { setFotoFile(null); setFotoPreview(null); }} className="text-sm text-[#64748b] text-center underline">
              Refazer foto
            </button>
          )}

          <button
            type="button"
            onClick={irParaStep2}
            disabled={!fotoPreview}
            className="w-full py-4 rounded-2xl bg-[#333A56] text-white font-bold text-base disabled:opacity-30 active:scale-[0.98] transition flex items-center justify-center gap-2"
          >
            Próximo <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ── Passo 2: Dados da passagem ── */}
      {step === 2 && (
        <div className="flex flex-col gap-4">
          <div className="text-center">
            <p className="font-bold text-lg text-[#333A56]">Dados da passagem</p>
            <p className="text-sm text-[#64748b] mt-1">Descreva o estado do serviço ao encerrar</p>
          </div>

          {/* Título */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] mb-2 uppercase tracking-wide">
              Título <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Turno diurno encerrado sem intercorrências…"
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#e8eaf0] text-sm text-[#333A56] placeholder:text-[#94a3b8] focus:outline-none focus:border-[#333A56]/40"
            />
          </div>

          {/* Prioridade */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] mb-2 uppercase tracking-wide">
              Prioridade
            </label>
            <div className="grid grid-cols-3 gap-2">
              {PRIOR_PASS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPrioridade(p.value)}
                  className="py-2.5 rounded-xl text-sm font-semibold border transition active:scale-[0.97]"
                  style={
                    prioridade === p.value
                      ? { background: p.bg, borderColor: p.border, color: p.text }
                      : { background: "#ffffff", borderColor: "#e8eaf0", color: "#64748b" }
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] mb-2 uppercase tracking-wide">
              Descrição <span className="text-[#94a3b8]">(opcional)</span>
            </label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva a situação do serviço, ocorrências ou pendências…"
              rows={4}
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#e8eaf0] text-sm text-[#333A56] placeholder:text-[#94a3b8] focus:outline-none focus:border-[#333A56]/40 resize-none"
            />
          </div>

          {/* Vigilante que assume */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] mb-2 uppercase tracking-wide">
              Vigilante que assume o turno
            </label>
            <select
              value={paraVigilanteNome}
              onChange={(e) => setParaVigilanteNome(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#e8eaf0] text-sm text-[#333A56] focus:outline-none focus:border-[#333A56]/40 appearance-none"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
            >
              <option value="">Selecione o vigilante…</option>
              {optsVigilantes.map((u) => (
                <option key={u.id} value={u.nome}>{u.nome}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(1)} className="flex-none px-4 py-4 rounded-2xl bg-white border border-[#e8eaf0] text-[#64748b] active:scale-[0.98] transition">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={irParaStep3}
              className="flex-1 py-4 rounded-2xl bg-[#333A56] text-white font-bold text-base active:scale-[0.98] transition flex items-center justify-center gap-2"
            >
              Próximo <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Passo 3: Material + Confirmar ── */}
      {step === 3 && (
        <div className="flex flex-col gap-4">
          <div className="text-center">
            <p className="font-bold text-lg text-[#333A56]">Material e confirmação</p>
            <p className="text-sm text-[#64748b] mt-1">Marque o material e confirme a passagem</p>
          </div>

          {/* Foto thumbnail */}
          {fotoPreview && (
            <div
              className="w-24 h-24 rounded-2xl overflow-hidden border border-[#e8eaf0] mx-auto cursor-pointer active:scale-95 transition shadow-sm"
              onClick={() => setFotoAmpliada(true)}
            >
              <img src={fotoPreview} alt="foto" className="w-full h-full object-cover" />
            </div>
          )}

          {/* Resumo da passagem */}
          <div className="rounded-2xl bg-white border border-[#e8eaf0] divide-y divide-[#e8eaf0]">
            <div className="px-4 py-3">
              <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Vigilante</p>
              <p className="font-semibold text-[#333A56] mt-0.5">{user?.nome}</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Título</p>
              <p className="font-semibold text-[#333A56] mt-0.5">{titulo}</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Prioridade</p>
              <p className="font-semibold mt-0.5" style={{ color: priorCfg?.text }}>{priorCfg?.label}</p>
            </div>
            {paraVigilanteNome.trim() && (
              <div className="px-4 py-3">
                <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Para vigilante</p>
                <p className="font-semibold text-[#333A56] mt-0.5">{paraVigilanteNome.trim()}</p>
              </div>
            )}
            <div className="px-4 py-3">
              <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Data</p>
              <p className="font-semibold text-[#333A56] mt-0.5">
                {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
              </p>
            </div>
          </div>

          {/* Material de carga */}
          {(() => {
            const confirmados = MATERIAL_LISTA.filter((i) => material[i]).length;
            const total = MATERIAL_LISTA.length;
            const pct = Math.round((confirmados / total) * 100);
            return (
              <>
                <div className="rounded-xl bg-white border border-[#e8eaf0] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wide">Material confirmado</span>
                    <span className="text-[12px] font-bold" style={{ color: confirmados === total ? "#16a34a" : "#333A56" }}>
                      {confirmados}/{total}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-[#e8eaf0] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${pct}%`, background: confirmados === total ? "#16a34a" : "#333A56" }}
                    />
                  </div>
                </div>

                <div className="rounded-2xl bg-white border border-[#e8eaf0] overflow-hidden divide-y divide-[#e8eaf0]">
                  {MATERIAL_LISTA.map((item) => (
                    <label
                      key={item}
                      className="flex items-center gap-3 px-4 py-3.5 cursor-pointer active:bg-[#f4f5f8] select-none"
                      onClick={() => toggleItem(item)}
                    >
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition ${material[item] ? "bg-emerald-600 border-emerald-600" : "border-[#e8eaf0]"}`}>
                        {material[item] && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <span className={`text-sm transition ${material[item] ? "text-[#333A56] font-medium" : "text-[#64748b]"}`}>{item}</span>
                    </label>
                  ))}
                </div>
              </>
            );
          })()}

          {/* Observações */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] mb-2 uppercase tracking-wide">
              Observações <span className="text-[#94a3b8]">(opcional)</span>
            </label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Itens extras, divergências ou observações gerais…"
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#e8eaf0] text-sm text-[#333A56] placeholder:text-[#94a3b8] focus:outline-none focus:border-[#333A56]/40 resize-none"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={saving}
              className="flex-none px-4 py-4 rounded-2xl bg-white border border-[#e8eaf0] text-[#64748b] active:scale-[0.98] transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={confirmar}
              disabled={saving}
              className="flex-1 py-4 rounded-2xl bg-emerald-600 text-white font-bold text-base active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {saving ? "Enviando…" : "Confirmar Passagem"}
            </button>
          </div>
        </div>
      )}

      {/* Foto ampliada */}
      {fotoAmpliada && (
        <div
          className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setFotoAmpliada(false)}
        >
          <img src={fotoPreview} alt="foto ampliada" className="max-w-full max-h-full object-contain rounded-2xl" />
        </div>
      )}
    </MobileLayout>
  );
}
